import { createContext, useState, useEffect, useContext } from 'react'

import { syncBuilds } from '../utils/buildsCloud'

export const AuthContext = createContext()

const API = 'https://api.roprime-simulator.com/api/auth'

async function apiFetch(path, options = {}) {
	const res = await fetch(API + path, {
		headers: { 'Content-Type': 'application/json', ...options.headers },
		...options,
	})
	const data = await res.json().catch(() => ({}))
	return { ok: res.ok, status: res.status, data }
}

export const AuthProvider = ({ children }) => {
	const [user, setUser]   = useState(null)
	const [token, setToken] = useState(() => localStorage.getItem('authToken'))
	const [loading, setLoading] = useState(true)

	// Restore session on mount
	useEffect(() => {
		if (!token) { setLoading(false); return }
		apiFetch('/me', { headers: { Authorization: `Bearer ${token}` } })
			.then(({ ok, data }) => {
				if (ok) {
					setUser(data)
					syncBuilds(token)
				} else clearSession()
			})
			.finally(() => setLoading(false))
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	function saveSession(newToken, newUser) {
		localStorage.setItem('authToken', newToken)
		setToken(newToken)
		setUser(newUser)
	}

	function clearSession() {
		localStorage.removeItem('authToken')
		setToken(null)
		setUser(null)
	}

	async function register(username, email, password) {
		const { ok, data } = await apiFetch('/register', {
			method: 'POST',
			body: JSON.stringify({ username, email, password }),
		})
		if (!ok) throw new Error(data.error || 'Registration failed')
		return data.message
	}

	async function login(email, password) {
		const { ok, status, data } = await apiFetch('/login', {
			method: 'POST',
			body: JSON.stringify({ email, password }),
		})
		if (!ok) {
			const err = new Error(data.error || 'Login failed')
			err.code = data.code
			err.status = status
			throw err
		}
		saveSession(data.token, data.user)
		syncBuilds(data.token)
	}

	async function logout() {
		if (token)
			await apiFetch('/logout', {
				method: 'POST',
				headers: { Authorization: `Bearer ${token}` },
			}).catch(() => {})
		clearSession()
	}

	async function resendVerification(email) {
		const { data } = await apiFetch('/resend-verification', {
			method: 'POST',
			body: JSON.stringify({ email }),
		})
		return data.message
	}

	async function forgotPassword(email) {
		const { data } = await apiFetch('/forgot-password', {
			method: 'POST',
			body: JSON.stringify({ email }),
		})
		return data.message
	}

	async function resetPassword(resetToken, password) {
		const { ok, data } = await apiFetch('/reset-password', {
			method: 'POST',
			body: JSON.stringify({ token: resetToken, password }),
		})
		if (!ok) throw new Error(data.error || 'Reset failed')
		return data.message
	}

	async function verifyEmail(verifyToken) {
		const { ok, data } = await apiFetch('/verify?token=' + encodeURIComponent(verifyToken))
		if (!ok) throw new Error(data.error || 'Verification failed')
		return data.message
	}

	async function changePassword(currentPassword, newPassword) {
		const { ok, data } = await apiFetch('/change-password', {
			method: 'POST',
			headers: { Authorization: `Bearer ${token}` },
			body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
		})
		if (!ok) throw new Error(data.error || 'Change failed')
		return data.message
	}

	async function changeEmail(email, password) {
		const { ok, data } = await apiFetch('/change-email', {
			method: 'POST',
			headers: { Authorization: `Bearer ${token}` },
			body: JSON.stringify({ email, password }),
		})
		if (!ok) throw new Error(data.error || 'Change failed')
		clearSession()
		return data.message
	}

	async function changeUsername(username) {
		const { ok, data } = await apiFetch('/change-username', {
			method: 'POST',
			headers: { Authorization: `Bearer ${token}` },
			body: JSON.stringify({ username }),
		})
		if (!ok) throw new Error(data.error || 'Change failed')
		setUser(prev => ({ ...prev, username: data.username }))
		return data.message
	}

	async function deleteAccount(password) {
		const { ok, data } = await apiFetch('/account', {
			method: 'DELETE',
			headers: { Authorization: `Bearer ${token}` },
			body: JSON.stringify({ password }),
		})
		if (!ok) throw new Error(data.error || 'Deletion failed')
		clearSession()
		return data.message
	}

	return (
		<AuthContext.Provider value={{
			user,
			token,
			loading,
			isAuthenticated: !!user,
			register,
			login,
			logout,
			resendVerification,
			forgotPassword,
			resetPassword,
			verifyEmail,
			changePassword,
			changeEmail,
			changeUsername,
			deleteAccount,
		}}>
			{children}
		</AuthContext.Provider>
	)
}

export function useAuth() {
	return useContext(AuthContext)
}
